import { clinicApi, couponApi } from "@api";
import {
  ComponentPagination,
  SearchFilter,
  SelectedFilter,
  ComponentHeader,
  ComponentBread,
  StyledInnerListPage,
  StyledOuterListPage,
  SharedButton,
  SharedIconButton,
  PlusIcon,
  DownloadV2,
} from "@components";

import {
  PATH_COUPON_FAMILY,
  PATH_COUPON_LIST,
  couponFamilyFilter,
  enumFilter,
  initPage,
} from "@configs";
import { IAdvanceSearch, ICouponFamily, IPagination } from "@interfaces";
import { CouponFamilyTable } from "@modules";
import { selectApp, setLoading } from "@redux";
import { useNotify } from "@utils";
import { Tooltip } from "antd";

import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { AdvanceFilter } from "@modules";
import { PlusOutlined } from "@ant-design/icons";
import { ExportSidebar } from "./Export";

export const PageCouponFamily = () => {
  // hook
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { success, error } = useNotify();
  // state
  const [page, setPage] = useState<IPagination>(initPage);
  const [total, setTotal] = useState<number>(0);
  const [data, setData] = useState<ICouponFamily[]>([]);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState<string>("");
  const [date, setDate] = useState<string[]>([]);
  const [isAdvance, setIsAdvance] = useState<boolean>(false);
  const [advance, setAdvance] = useState<IAdvanceSearch>();
  const { mobile } = useSelector(selectApp);
  const steps = [
    {
      title: <a href={PATH_COUPON_LIST}>{t("page.coupon_management")}</a>,
    },
    {
      title: <a href={PATH_COUPON_FAMILY}>{t("page.selected_program_name")}</a>,
    },
  ];

  useEffect(() => {
    fetchCoupon();
  }, [id, page, text, date, advance]);

  const fetchCoupon = async () => {
    try {
      dispatch(setLoading(true));
      const res = await couponApi.coupon(
        {
          page: page.page,
          limit: page.limit,
          ...(filters && { searchColumn: filters }),
          ...(text && { search: text }),
          ...(date.length > 1 && { startDate: date[0] }),
          ...(date.length > 1 && { endDate: date[1] }),
          ...(advance && advance),
        },
        id
      );
      setData(res.data?.data);
      setTotal(res.data?.nTotal);
      success(t("message.fetch.success"));
    } catch (err: any) {
      error(t("message.fetch.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };
  const handleDateFilter = (dates: null | (Dayjs | null)[], dateStrings: string[]) => {
    setDate(dateStrings);
  };
  const handlePagination = (current: number, size: number) => {
    setPage({ ...page, page: current, limit: size });
  };

  const changeFilter = (value: string) => {
    setFilters(value);
    setText("");
  };
  const searchText = (value: string) => setText(value);
  const handleAdvance = () => setIsAdvance(!isAdvance);
  const handleClearSearch = () => {
    setText("");
    setFilters("");
  };
  const handleClearDate = () => setDate([]);
  const button = (
    <Tooltip title={t("page.add_advance_filter")}>
      <SharedButton type="primary" size="middle" onClick={handleAdvance}>
        {t("page.advance_filter")}
      </SharedButton>
    </Tooltip>
  );
  const handleAdvanceFilter = (params: IAdvanceSearch) => {
    setAdvance(params);
    handleAdvance();
  };
  const handleDownloadCsv = async () => {
    try {
      const { data } = await couponApi.exportActiveExcel(id, {
        searchColumn: filters,
        search: text,
        startDate: date[0],
        endDate: date[1],
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: `text/csv` }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "export redeem coupon");
      link.click();
      window.URL.revokeObjectURL(url);
      success(t("message.download.success"));
    } catch (err: any) {
      error(t("message.download.fail"));
      throw new Error(err);
    }
  };
  const downloadButton = (
    <>
      <Tooltip title={t("page.download_active_coupon_excel")}>
        <SharedButton
          type="primary"        
          icon={<DownloadV2 />}
          size="middle"
            onClick={handleDownloadCsv}
            style={{background: "#00AA67", color:"#fff"}}
        >
          {t("page.download_coupons")}
        </SharedButton>
      </Tooltip>
    </>
  );
  return (
    <>
      <ComponentHeader title={t("page.coupon_management")} />
      <StyledOuterListPage>
        <ComponentBread pages={steps} />
        <StyledInnerListPage>
          <SearchFilter
            filter={couponFamilyFilter}
            actionBtn={downloadButton}
            callbackFun={changeFilter}
            onDateChange={handleDateFilter}
            searchText={searchText}
            searchInputPlaceholder={t("page.search_user_by")}
            selectedOption={filters}
            advanceBtn={button}
          />
          <SelectedFilter
            handleClearSearch={handleClearSearch}
            selected={filters}
            formDate={date[0]}
            toDate={date[1]}
          />
          <CouponFamilyTable data={data} callback={fetchCoupon} />
          <ComponentPagination total={total} page={page.page} changePage={handlePagination} />
        </StyledInnerListPage>
        <AdvanceFilter visible={isAdvance} callback={handleAdvanceFilter} />
      </StyledOuterListPage>
    </>
  );
};
